/**
 * Admin Notifications Page
 * Full view of all platform notifications with filtering and management
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Bell,
  CheckCircle,
  CurrencyDollar,
  Funnel,
  GearSix,
  MagnifyingGlass,
  Trash,
  UsersThree
} from '@phosphor-icons/react';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Sample notifications (in real app, fetch from backend)
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: 'user',
      title: 'New user registration',
      message: 'John Doe signed up for Pro plan',
      time: '5 minutes ago',
      timestamp: new Date(Date.now() - 5 * 60 * 1000),
      read: false
    },
    {
      id: 2,
      type: 'payment',
      title: 'Payment received',
      message: 'Subscription payment of $299 processed',
      time: '1 hour ago',
      timestamp: new Date(Date.now() - 60 * 60 * 1000),
      read: false
    },
    {
      id: 3,
      type: 'system',
      title: 'System update',
      message: 'Platform updated to version 2.5.0',
      time: '3 hours ago',
      timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000),
      read: true
    },
    {
      id: 4,
      type: 'user',
      title: 'User upgraded plan',
      message: 'Jane Smith upgraded from Basic to Partner Pro',
      time: '5 hours ago',
      timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000),
      read: true
    },
    {
      id: 5,
      type: 'payment',
      title: 'Payment failed',
      message: 'Failed payment attempt for user alice@example.com',
      time: '1 day ago',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
      read: true
    },
    {
      id: 6,
      type: 'system',
      title: 'Backup completed',
      message: 'Daily database backup completed successfully',
      time: '1 day ago',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
      read: true
    },
  ]);

  const getIcon = (type) => {
    switch (type) {
      case 'user':
        return { Icon: UsersThree, color: 'blue' };
      case 'payment':
        return { Icon: CurrencyDollar, color: 'green' };
      case 'system':
        return { Icon: GearSix, color: 'purple' };
      default:
        return { Icon: Bell, color: 'gray' };
    }
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      green: 'bg-green-500/20 text-green-400 border-green-500/30',
      purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      gray: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return colors[color] || colors.gray;
  };

  const markAsRead = (id) => {
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, read: true } : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
  };

  const deleteNotification = (id) => {
    setNotifications(notifications.filter(n => n.id !== id));
  };

  const filteredNotifications = notifications.filter(n => {
    const matchesType = filterType === 'all' || n.type === filterType;
    const matchesSearch = n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          n.message.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Dashboard</span>
          </button>
        </div>
      </div>

      {/* Page Title */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center">
              <Bell size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Notifications</h1>
              <p className="text-gray-400 text-sm">
                {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          <button
            onClick={markAllAsRead}
            disabled={unreadCount === 0}
            className="px-4 py-2 bg-[#6366F1] hover:bg-[#5558E3] disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg transition-colors text-sm font-medium"
          >
            Mark all as read
          </button>
        </div>
      </div>

      {/* Filters & MagnifyingGlass */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* MagnifyingGlass */}
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Search notifications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
            />
          </div>

          {/* Type Funnel */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'all'
                  ? 'bg-[#6366F1] text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterType('user')}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'user'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              UsersThree
            </button>
            <button
              onClick={() => setFilterType('payment')}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'payment'
                  ? 'bg-green-500 text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              Payments
            </button>
            <button
              onClick={() => setFilterType('system')}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'system'
                  ? 'bg-purple-500 text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              System
            </button>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-3">
        {filteredNotifications.length > 0 ? (
          filteredNotifications.map((notification) => {
            const { Icon, color } = getIcon(notification.type);
            
            return (
              <div
                key={notification.id}
                className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 transition-all hover:bg-white/10 ${
                  !notification.read ? 'border-l-4 border-l-[#6366F1]' : ''
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`p-3 rounded-xl border ${getColorClasses(color)}`}>
                    <Icon size={20} />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-white font-medium mb-1">
                          {notification.title}
                        </h3>
                        <p className="text-gray-400 text-sm">
                          {notification.message}
                        </p>
                      </div>
                      
                      {!notification.read && (
                        <span className="px-2 py-1 bg-[#6366F1]/20 text-[#6366F1] text-xs rounded-full">
                          New
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-gray-500">
                        {notification.time}
                      </span>

                      <div className="flex items-center gap-2">
                        {!notification.read && (
                          <button
                            onClick={() => markAsRead(notification.id)}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                            title="Mark as read"
                          >
                            <CheckCircle size={16} className="text-green-400" />
                          </button>
                        )}
                        <button
                          onClick={() => deleteNotification(notification.id)}
                          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash size={16} className="text-red-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-12 text-center">
            <Bell size={48} className="text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No notifications found</h3>
            <p className="text-gray-400 text-sm">
              {searchQuery || filterType !== 'all'
                ? 'Try adjusting your filters or search query'
                : 'You\'re all caught up!'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
